import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Github, Link, Unlink, Loader2, ExternalLink, Star, GitFork, Lock, Globe, RefreshCw, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GitHubIntegration = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [status, setStatus] = useState(null);
  const [repos, setRepos] = useState([]);
  const [token, setToken] = useState('');
  const [activeTab, setActiveTab] = useState('connect');
  const [copiedRepo, setCopiedRepo] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchStatus();
    }
  }, [isOpen]);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/github/status`);
      setStatus(response.data);
      if (response.data.connected) {
        setActiveTab('repos');
        fetchRepos();
      }
    } catch (error) {
      console.error('Error fetching GitHub status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRepos = async () => {
    try {
      const response = await axios.get(`${API}/github/repos`);
      setRepos(response.data.repositories);
    } catch (error) {
      console.error('Error fetching repos:', error);
    }
  };

  const handleConnect = async () => {
    if (!token.trim()) {
      toast.error('Por favor ingresa tu token de GitHub');
      return;
    }

    setConnecting(true);
    try {
      const response = await axios.post(`${API}/github/connect`, { github_token: token });
      toast.success('GitHub conectado correctamente');
      setStatus({ connected: true, username: response.data.github_username, avatar: response.data.github_avatar });
      setToken('');
      setActiveTab('repos');
      fetchRepos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al conectar');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('¿Desconectar tu cuenta de GitHub?')) return;
    
    try {
      await axios.delete(`${API}/github/disconnect`);
      toast.success('GitHub desconectado');
      setStatus({ connected: false });
      setRepos([]);
      setActiveTab('connect');
    } catch (error) {
      toast.error('Error al desconectar');
    }
  };

  const copyToClipboard = (text, repoId) => {
    navigator.clipboard.writeText(text);
    setCopiedRepo(repoId);
    setTimeout(() => setCopiedRepo(null), 2000);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-2xl glass rounded-2xl overflow-hidden max-h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="github-modal"
        >
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <Github className="w-6 h-6 text-white" />
              <h2 className="text-xl font-bold text-white">Integración GitHub</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
          </div>

          {status?.connected && (
            <div className="flex border-b border-white/10">
              <button
                onClick={() => setActiveTab('repos')}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${activeTab === 'repos' ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]' : 'text-gray-400 hover:text-white'}`}
              >
                Repositorios
              </button>
              <button
                onClick={() => setActiveTab('connect')}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${activeTab === 'connect' ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]' : 'text-gray-400 hover:text-white'}`}
              >
                Configuración
              </button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#FF6B00]" />
              </div>
            ) : activeTab === 'connect' ? (
              <div className="space-y-6">
                {status?.connected ? (
                  <div className="glass rounded-xl p-6 border border-white/10">
                    <div className="flex items-center gap-4 mb-6">
                      {status.avatar && <img src={status.avatar} alt="" className="w-16 h-16 rounded-full" />}
                      <div>
                        <p className="text-white font-bold text-lg">@{status.username}</p>
                        <p className="text-green-400 text-sm flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-green-400" />
                          Conectado
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleDisconnect}
                      className="w-full py-2 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors flex items-center justify-center gap-2"
                      data-testid="disconnect-github-btn"
                    >
                      <Unlink className="w-4 h-4" />
                      Desconectar GitHub
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="glass rounded-xl p-6 border border-white/10">
                      <h3 className="text-white font-bold mb-4">Conectar con Token Personal</h3>
                      <p className="text-gray-400 text-sm mb-4">
                        Para conectar tu cuenta de GitHub, necesitas un Personal Access Token con permisos de <code className="text-[#FF6B00]">repo</code>.
                      </p>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm text-gray-400 block mb-2">Personal Access Token</label>
                          <input
                            type="password"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#FF6B00]/50 focus:outline-none"
                            data-testid="github-token-input"
                          />
                        </div>
                        
                        <button
                          onClick={handleConnect}
                          disabled={connecting}
                          className="w-full py-3 rounded-lg bg-gradient-to-r from-[#FF6B00] to-[#FF8533] text-white font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
                          data-testid="connect-github-btn"
                        >
                          {connecting ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <>
                              <Link className="w-5 h-5" />
                              Conectar GitHub
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    
                    <div className="glass rounded-xl p-4 border border-blue-500/20 bg-blue-500/5">
                      <p className="text-sm text-blue-300">
                        <strong>¿Cómo obtener un token?</strong><br />
                        1. Ve a <a href="https://github.com/settings/tokens/new" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">github.com/settings/tokens</a><br />
                        2. Crea un nuevo token con el scope "repo"<br />
                        3. Copia el token y pégalo aquí
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold">Tus Repositorios ({repos.length})</h3>
                  <button onClick={fetchRepos} className="text-gray-400 hover:text-white">
                    <RefreshCw className="w-5 h-5" />
                  </button>
                </div>
                
                {repos.length === 0 ? (
                  <div className="text-center py-12">
                    <Github className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                    <p className="text-gray-500">No se encontraron repositorios</p>
                  </div>
                ) : (
                  repos.map((repo) => (
                    <div key={repo.id} className="glass rounded-xl p-4 border border-white/10 hover:border-white/20 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {repo.private ? (
                              <Lock className="w-4 h-4 text-yellow-500" />
                            ) : (
                              <Globe className="w-4 h-4 text-green-500" />
                            )}
                            <a href={repo.url} target="_blank" rel="noopener noreferrer" className="text-white font-medium hover:text-[#FF6B00] flex items-center gap-1">
                              {repo.name}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                          {repo.description && <p className="text-sm text-gray-500 mb-2">{repo.description}</p>}
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            {repo.language && <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-400" />{repo.language}</span>}
                            <span className="flex items-center gap-1"><Star className="w-3 h-3" />{repo.stars}</span>
                            <span className="flex items-center gap-1"><GitFork className="w-3 h-3" />{repo.forks}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => copyToClipboard(repo.clone_url, repo.id)}
                          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                          title="Copiar URL de clonación"
                        >
                          {copiedRepo === repo.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
